'use client'
import React from 'react'
import Link from 'next/link'
import { FaLine, FaPhoneSquareAlt } from 'react-icons/fa'
import { IoLogoWhatsapp } from 'react-icons/io'

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatWhatsapp(raw) {
  if (!raw) return null
  // Already a full URL
  if (raw.startsWith('http')) return raw
  // Strip non-digits and build wa.me link
  const digits = raw.replace(/\D/g, '')
  return `https://wa.me/${digits}`
}

function formatLine(raw) {
  if (!raw) return null
  if (raw.startsWith('http')) return raw
  // Line ID → line.me deep link
  return `https://line.me/ti/p/~${raw.replace(/^@/, '')}`
}

function formatPhone(raw) {
  if (!raw) return null
  if (raw.startsWith('http') || raw.startsWith('tel:')) return raw
  return `tel:${raw.replace(/\s/g, '')}`
}

// ─── Scroll helper ────────────────────────────────────────────────────────────
function scrollToEnquiry(e) {
  e.preventDefault()
  const target = document.getElementById('target-anchor')
  if (!target) return
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// ─── Individual contact buttons ───────────────────────────────────────────────
function ContactBtn({ href, icon, label, bg, textColor }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col justify-center items-center py-1 px-2 rounded-md shadow-sm"
      style={{ background: bg, minWidth: 56, height: 50 }}
    >
      {icon}
      <span style={{ color: textColor, fontSize: 11, marginTop: 2, lineHeight: 1 }}>{label}</span>
    </Link>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────
// Props:
//   property   — full property object (property.agent holds contact info)
//   dictionary — dictionary.property (for translated labels)
//   config     — optional styling config from website.elements.propertyContact
//   lang       — current language string
const PropertyContact = ({ property, dictionary, config = {}, lang = 'en' }) => {
  const agent = property?.agent ?? {}

  const phoneUrl     = formatPhone(agent.agentPhone ?? agent.phone ?? agent.agentPhoneNumber)
  const whatsappUrl  = formatWhatsapp(agent.agentWhatsapp ?? agent.whatsapp)
  const lineUrl      = formatLine(agent.agentLine ?? agent.line ?? agent.agentLineId)

  // Nothing to show and no enquiry button → render nothing
  const hasAnyContact = phoneUrl || whatsappUrl || lineUrl
  if (!hasAnyContact && !dictionary?.sendAnEnquiry) return null

  const {
    show          = true,
    bgColor       = 'var(--color-main)',
    enquiryBg     = 'var(--color-secondary)',
    enquiryText   = '#ffffff',
    phoneBg       = '#ffffff',
    phoneColor    = 'var(--color-third, #B96A57)',
    whatsappBg    = '#25D366',
    lineBg        = '#06C755',
    iconColor     = '#ffffff',
  } = config

  if (!show) return null

  return (
    <div
      className="md:hidden z-[50] w-full fixed bottom-0 flex items-center gap-1 px-2 py-1 border-t border-t-secondary"
      style={{ background: bgColor }}
    >
      {/* ── Phone ── */}
      {phoneUrl && (
        <ContactBtn
          href={phoneUrl}
          bg={phoneBg}
          textColor={phoneColor}
          label={dictionary?.phone ?? 'Call'}
          icon={<FaPhoneSquareAlt size={22} color={phoneColor} />}
        />
      )}

      {/* ── WhatsApp ── */}
      {whatsappUrl && (
        <ContactBtn
          href={whatsappUrl}
          bg={whatsappBg}
          textColor={iconColor}
          label="WhatsApp"
          icon={<IoLogoWhatsapp size={22} color={iconColor} />}
        />
      )}

      {/* ── LINE ── */}
      {lineUrl && (
        <ContactBtn
          href={lineUrl}
          bg={lineBg}
          textColor={iconColor}
          label="LINE"
          icon={<FaLine size={22} color={iconColor} />}
        />
      )}

      {/* ── Enquiry CTA — takes remaining width ── */}
      <button
        onClick={scrollToEnquiry}
        className="flex-1 flex justify-center items-center rounded-md font-bold h-[50px] text-sm"
        style={{ background: enquiryBg, color: enquiryText }}
      >
        {dictionary?.sendAnEnquiry ?? 'Send an enquiry'}
      </button>
    </div>
  )
}

export default PropertyContact